import { ChevronUp } from 'lucide-react';
import {
  Bar,
  BarChart as BarChartRechart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartContainer } from '@/components/charts/ChartContainer';
import { cn } from '@/utils/classname';
import { abbreviateNumber } from '@/utils/misc.util';

interface Props {
  data: {
    name: string;
    [key: string]: string | number;
  }[];
  lines?: {
    datakey: string;
    lineClassName: string;
  }[];
  legend?: boolean;
  labelTop?: string;
  labelBottom?: string;
  renderTopLegends?: boolean;
  className?: string;
}

export const BarChart = ({
  data,
  lines = [],
  legend,
  labelTop,
  labelBottom,
  renderTopLegends,
  className,
}: Props) => {
  const labelExists = labelTop || labelBottom;
  return (
    <div className={cn('flex size-full flex-col', className)}>
      {renderTopLegends && (
        <div className="mb-2 flex flex-row-reverse justify-end gap-4">
          {lines.map((line, index) => {
            return (
              <div
                key={index}
                className="text-chariot-text-secondary flex items-center gap-2 text-xs"
              >
                <div
                  className={cn(
                    'h-[6px] w-6 rounded-md bg-current',
                    line?.lineClassName
                  )}
                ></div>
                {line.datakey}
              </div>
            );
          })}
        </div>
      )}
      <div className="relative size-full">
        {labelExists && (
          <div className="absolute top-0 left-0 flex h-full flex-col justify-center">
            {labelTop && (
              <p className="text-chariot-text-secondary absolute top-4 flex -translate-x-1/2 translate-y-full -rotate-90 items-center gap-1 text-xs font-bold text-nowrap">
                {labelTop}
                <ChevronUp className="mt-[2px] size-4 rotate-90" />
              </p>
            )}
            {labelBottom && (
              <p className="text-chariot-text-secondary absolute bottom-12 flex -translate-x-1/2 -translate-y-full -rotate-90 items-center gap-1 text-xs font-bold text-nowrap">
                <ChevronUp className="size-4 -rotate-90" />
                {labelBottom}
              </p>
            )}
          </div>
        )}
        <ChartContainer>
          <BarChartRechart
            data={data}
            stackOffset="sign"
            margin={{
              top: 12,
              right: 0,
              left: !labelExists ? -36 : 0,
              bottom: 0,
            }}
            barSize={8}
          >
            <CartesianGrid
              strokeDasharray="0 0"
              vertical={false}
              strokeWidth={2}
              className="stroke-chariot-stroke"
            />
            <XAxis
              dataKey="name"
              className="[&_line]:stroke-chariot-stroke [&_tspan]:fill-chariot-text-secondary text-xs font-normal"
              // tickLine={false}
              strokeWidth={2}
              minTickGap={40}
              tickMargin={4}
            />
            <YAxis
              className="[&_tspan]:fill-chariot-text-secondary text-xs font-normal"
              axisLine={false}
              tickLine={false}
              width={72}
              tickFormatter={(value: number) => {
                return abbreviateNumber(value).toString();
              }}
            />
            <Tooltip
              content={<DetailedTooltip />}
              cursor={{ className: 'fill-chariot-background-secondary' }}
            />
            {legend && <Legend />}
            {lines.map(({ datakey, lineClassName }) => (
              <Bar
                className={lineClassName}
                dataKey={datakey}
                key={datakey}
                stackId="stack"
              />
            ))}
          </BarChartRechart>
        </ChartContainer>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DetailedTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary mx-2 rounded-md border-2 p-2">
        <p style={{ fontWeight: 'bold' }}>{label}</p>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {(payload as any[]).map((entry, index) => {
          if (entry.value === 0) {
            return null;
          }

          return (
            <p
              key={`item-${index}`}
              className={entry.className || entry?.payload?.className}
            >
              {entry.name}: {Math.abs(entry.value)}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};
